import { supabase } from '../supabaseClient';

const PROXY_FUNCTION = 'lemon-squeezy-proxy';

const invokeProxy = async (body) => {
  const { data, error } = await supabase.functions.invoke(PROXY_FUNCTION, {
    body
  });

  if (error) throw error;

  // Handle Lemon Squeezy API errors (they return { errors: [...] })
  if (data?.errors) {
    const err = new Error(data.errors?.[0]?.detail || 'Lemon Squeezy API error');
    err.response = { data };
    throw err;
  }

  return data;
};

export const lemonSqueezyService = {
  // Fetch all products
  async getProducts() {
    console.log('🔍 Fetching Lemon Squeezy products...');
    try {
      const data = await invokeProxy({ action: 'products' });

      if (data?.data) {
        console.log('✅ Products details:', data.data.map(product => ({
          id: product.id,
          name: product.attributes?.name,
          slug: product.attributes?.slug,
          price: product.attributes?.price_formatted,
          status: product.attributes?.status,
          createdAt: product.attributes?.created_at
        })));
      }

      return data?.data || [];
    } catch (error) {
      console.error('❌ Error fetching products:', {
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Fetch a specific product
  async getProduct(productId) {
    console.log('🔍 Fetching product details:', productId);
    try {
      const data = await invokeProxy({ action: 'product', productId });
      console.log('✅ Product fetched:', data?.data);
      return data?.data;
    } catch (error) {
      console.error('❌ Error fetching product:', {
        productId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Create a checkout session
  async createCheckout(variantId, userId) {
    console.log('🛍️ Starting checkout creation:', { variantId, userId });

    try {
      if (!variantId) throw new Error('Variant ID is required');
      if (!userId) throw new Error('User ID is required');

      const data = await invokeProxy({
        action: 'checkout',
        variantId: variantId.toString(),
        userId,
        redirectUrl: `${window.location.origin}/workspace`
      });

      const checkout = data?.data;
      console.log('✅ Checkout created:', checkout);
      return checkout;
    } catch (error) {
      console.error('❌ Checkout error:', error);
      throw error;
    }
  },

  // Get subscription details
  async getSubscription(subscriptionId) {
    console.log('🔍 Fetching subscription:', subscriptionId);
    try {
      const data = await invokeProxy({ action: 'subscription', subscriptionId });
      console.log('✅ Subscription fetched:', data?.data);
      return data?.data;
    } catch (error) {
      console.error('❌ Error fetching subscription:', {
        subscriptionId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Cancel subscription
  async cancelSubscription(subscriptionId) {
    console.log('🚫 Cancelling subscription:', subscriptionId);
    try {
      const data = await invokeProxy({ action: 'cancelSubscription', subscriptionId });
      console.log('✅ Subscription cancelled:', data);
      return data;
    } catch (error) {
      console.error('❌ Error cancelling subscription:', {
        subscriptionId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  },

  // Fetch variants for a product
  async getVariants(productId) {
    console.log('🔍 Fetching variants for product:', productId);
    try {
      const data = await invokeProxy({ action: 'variants', productId });
      const variants = data?.data || [];

      console.log('✅ Variants details:', variants.map(variant => ({
        id: variant.id,
        name: variant.attributes?.name,
        price: variant.attributes?.price_formatted,
        description: variant.attributes?.description,
        status: variant.attributes?.status,
        sort: variant.attributes?.sort
      })));

      return variants;
    } catch (error) {
      console.error('❌ Error fetching variants:', {
        productId,
        message: error.message,
        response: error.response?.data
      });
      throw error;
    }
  }
};
